import React, { useMemo } from 'react';
// MUI
import { Box, Typography, Card, CardContent, FormControlLabel, Checkbox } from '@mui/material';
// Interfaces
import { CompanyApplication, Company, Contact } from '../../../interfaces/users.interface';
import { useWorkflowMap } from '../../../hooks/useWorkflowMap';
import { Grantee } from '../../../interfaces/engagements';
import { BADGE_IMAGE_DATA_MAP, BLUE_BADGE_LABEL, GOLD_BADGE_LABEL, GOLD_NEXT_GEN_BADGE_LABEL, GOLD_STAR_BADGE_LABEL, SILVER_BADGE_LABEL } from '../../../services/badge';
import { isWorkflowFiveThreeOneOrGreater } from '../../../services/questionnaires';
import { WorkflowTypeKey } from '../../../interfaces/workflow.interface';

const ShieldLabel = ({ selectedReportTypes, blueBadgeSrc }: { selectedReportTypes: string[], blueBadgeSrc?: string }) => {
  const highestAssessmentBadge = useMemo(() => {
    if (selectedReportTypes.includes(GOLD_STAR_BADGE_LABEL)) {
      return BADGE_IMAGE_DATA_MAP.goldStar;
    }
    if (selectedReportTypes.includes(GOLD_NEXT_GEN_BADGE_LABEL)) {
      return BADGE_IMAGE_DATA_MAP.goldNextGen;
    }
    if (selectedReportTypes.includes(SILVER_BADGE_LABEL)) {
      return BADGE_IMAGE_DATA_MAP.silver;
    }
    if (selectedReportTypes.includes(GOLD_BADGE_LABEL)) {
      return BADGE_IMAGE_DATA_MAP.gold;
    }
    return null;
  }, [selectedReportTypes]);
  
  return (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
        }}>
          {selectedReportTypes.includes(BLUE_BADGE_LABEL) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  border: '1px solid #ccc',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  overflow: 'hidden',
                }}
              >
                <img
                  src={blueBadgeSrc || BADGE_IMAGE_DATA_MAP.blue.src}
                  alt={BADGE_IMAGE_DATA_MAP.blue.alt}
                  style={{
                    width: '80%',
                    height: '80%',
                    objectFit: 'contain',
                    padding: '2px',
                  }}
                />
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 400, color: '#333' }}>
                {BADGE_IMAGE_DATA_MAP.blue.label}
              </Typography>
            </Box>
          )}

          {highestAssessmentBadge && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  border: '1px solid #ccc',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  overflow: 'hidden',
                }}
                >
                <img
                  src={highestAssessmentBadge.src}
                  alt={highestAssessmentBadge.alt}
                  style={{
                    width: '80%',
                    height: '80%',
                    objectFit: 'contain',
                  }}
                />
               </Box>
              <Typography variant="body1" sx={{ fontWeight: 400, color: '#333' }}>
                {highestAssessmentBadge.label}
              </Typography>
            </Box>
          )}
        </Box>
  );
};

const ReviewAndConfirm = (
  { 
    type,
    company,
    selectedReportTypes,
    siteOrApplication, 
    questionnaireType,
    termsAccepted,
    setTermsOpen,
    isIndefinite,
    startDate,
    endDate,
    selectedEmployees,
  }: {
    selectedReportTypes: string[];
    type: string;
    company: Company | null;
    siteOrApplication: Contact | CompanyApplication | null;
    questionnaireType: WorkflowTypeKey | null;
    employeeList: Grantee[];
    selectedEmployees?: Grantee[];
    termsAccepted: boolean;
    setTermsOpen: (open: boolean) => void;
    isIndefinite: boolean;
    startDate: Date | null;
    endDate: Date | null;
  },
) => {
  const workflowMap = useWorkflowMap();
  const blueBadgeSrc = questionnaireType && isWorkflowFiveThreeOneOrGreater(questionnaireType) ? BADGE_IMAGE_DATA_MAP.blueNextGen.src : BADGE_IMAGE_DATA_MAP.blue.src;

  return (
        <>
        <Typography variant="h6">
          Review Your Selections
        </Typography>
        <Card
          sx={{
            p: 3,
            background: 'linear-gradient(135deg, #FEFEFE, #fcfcfd)',
            borderRadius: 2,
            boxShadow: 1,
            borderTop: '8px solid #1195CD',
            mb:2,
          }}
        >
        <CardContent>
          {/* Company Section - Label on Top, Logo & Name on Same Row */}
          <Box sx={{ mb:3 }}>
            <Typography variant="subtitle2" sx={{ color: '#666', textTransform: 'uppercase', fontWeight: 300 }}>
              Company
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
              {company?.logo ? (
                <img
                  src={company.logo}
                  alt={company.name}
                  style={{ width: 40, height: 40, borderRadius: '50%' }}
                />
              ) : (
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: '#ddd' }} />
              )}
              <Typography variant="h6" sx={{ fontWeight: 400, color: '#333' }}>
                {company?.name || ''}
              </Typography>
            </Box>
          </Box>

          {/* Site/Application */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ color: '#666', textTransform: 'uppercase', fontWeight: 300 }}>
              {type === 'site' ? 'Site' : !!type ? 'Application' : ''}
            </Typography>
            { siteOrApplication && (
              <Typography variant="body1" sx={{ fontWeight: 400, color: '#333' }}>
              {
              'application' in siteOrApplication
                ? (siteOrApplication).application.name 
                : (siteOrApplication).name}
            </Typography>
            )}
          </Box>

          {/* Questionnaire Version */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ color: '#666', textTransform: 'uppercase', fontWeight: 300 }}>
              Questionnaire Type
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 400, color: '#333' }}>
              {workflowMap[questionnaireType || '']?.version || ''}
            </Typography>
          </Box>
          {/* Company Section - Label on Top, Logo & Name on Same Row */}
          <Box sx={{ mb: 3 }}>
            <Typography
                variant="subtitle2"
                sx={{ color: '#666', textTransform: 'uppercase', fontWeight: 300 }}
            >
                Authorized Users
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mt: 1 }}>
                {selectedEmployees?.length ? (
                  selectedEmployees.map((employee) => (
                    <Box
                    key={employee.email}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2,
                      py: 0.8,
                      borderRadius: '6px',
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                      backgroundColor: '#f7f9f9',
                      boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
                    }}
                    >
                    <Typography variant="body1" sx={{ fontWeight: 400, color: '#333' }}>
                        {`${employee.email} (${employee.firstName || 'Unknown'} ${employee.lastName})`}
                    </Typography>
                    </Box>
                  ))
                ) : (
                <Typography></Typography>
                )}
            </Box>
            </Box>
          {/* Shields */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ color: '#666', textTransform: 'uppercase', fontWeight: 300 }}>
              Reports
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 400, color: '#333', pt: .5 }}>
              { siteOrApplication && <ShieldLabel selectedReportTypes={selectedReportTypes} blueBadgeSrc={blueBadgeSrc} /> }
            </Typography>
          </Box>

          {/* Start and End Date */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ color: '#666', textTransform: 'uppercase', fontWeight: 300 }}>
              Access Duration
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 400, color: '#333' }}>
              { (!startDate && !endDate && !isIndefinite) ? '' :
                isIndefinite
                  ? `${startDate ? new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Invalid Date'} - Indefinite`
                  : `${startDate ? new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Invalid Date'} to ${endDate ? new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Invalid Date'}`
              }
            </Typography>
          </Box>
          {/* Terms & Conditions Checkbox */}
          <Box sx={{ mt: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsAccepted}
                  disabled
                />
              }
              label={
                <Typography variant="body2">
                 You must read and agree to the Terms and Conditions.{' '}
                  <Typography
                    component="span"
                    color="primary"
                    variant='body2'
                    sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => setTermsOpen(true)}
                  >
                   Click  here
                  </Typography>
                  {' '}to view them.
                </Typography>
              }
            />
          </Box>
        </CardContent>
      </Card>
    </>
  );
};

export default ReviewAndConfirm;